import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import CarrierTag from "./CarrierTag";
import AffirmPromo from "./AffirmPromo";
import { weeklyLease } from "../lib/lease";

export default function ProductCard({ product, index = 0 }) {
  const variants = product.variants || [];
  const cheapest = variants.length
    ? variants.reduce((a, b) => ((b.price || Infinity) < (a.price || Infinity) ? b : a))
    : null;
  const showHint = cheapest && cheapest.condition && cheapest.price;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-line bg-white transition-all duration-300 hover:-translate-y-1 hover:border-ink hover:shadow-xl"
    >
      <div className="relative aspect-square overflow-hidden bg-alt">
        <Link to={`/product/${product.id}`} data-testid={`product-image-link-${product.id}`}>
          <img
            src={product.image_url}
            alt={product.name}
            className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-600">
          <BadgeCheck className="h-4 w-4" /> {product.condition}
        </div>
        <Link to={`/product/${product.id}`} data-testid={`product-title-link-${product.id}`}>
          <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-ink transition-colors hover:text-crimson">{product.name}</h3>
        </Link>
        <p className="mt-1 text-sm text-muted-foreground">{[product.storage, product.color].filter(Boolean).join(" · ")}</p>
        {product.carrier && (
          <div className="mt-2">
            <CarrierTag carrier={product.carrier} />
          </div>
        )}
        <div className="mt-auto pt-4">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Lease from</div>
          <div className="font-display text-2xl font-black text-crimson">${weeklyLease(product.price)}<span className="text-sm font-bold text-muted-foreground">/wk</span></div>
          {showHint && (
            <div className="mt-1 text-xs font-semibold text-ink" data-testid={`card-lowprice-${product.id}`}>
              Buy from <span className="text-crimson">${cheapest.price}</span> in {cheapest.condition} condition
            </div>
          )}
          <AffirmPromo amount={product.price} className="mt-1 text-xs" testId={`product-affirm-promo-${product.id}`} />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Link
            to="/apply"
            data-testid={`product-lease-${product.id}`}
            className="whitespace-nowrap rounded-full border border-ink px-3 py-2 text-center text-sm font-bold text-ink transition-colors hover:bg-ink hover:text-white"
          >
            Lease
          </Link>
          {product.in_stock ? (
            <Link
              to={`/product/${product.id}`}
              data-testid={`product-buy-${product.id}`}
              className="whitespace-nowrap rounded-full bg-ink px-3 py-2 text-center text-sm font-bold text-white transition-colors hover:bg-crimson"
            >
              Buy Outright
            </Link>
          ) : (
            <span
              data-testid={`product-soldout-${product.id}`}
              className="cursor-not-allowed whitespace-nowrap rounded-full bg-line px-3 py-2 text-center text-sm font-bold text-muted-foreground"
            >
              Sold Out
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
